import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal, Box, Button, Tooltip } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { apiRequest } from '@/src/services';
import { Constants, getZeroOption, type Option } from '@/src/utils';
import { examAssignmentSchema } from '../models/schema';
import {
    Loading,
    ControlledSingleSelect,
    ControlledChipMultiSelect,
    ControlledChipDisplay,
    SimpleButton,
    ErrorMessage,
    StatusMessage,
    SyncIcon,
} from '@/src/ui';
import type {
    ClassAssignmentDTO,
    ExamAssignmentCreateDTO,
    ExamAssignmentFullDTO,
    ExamAssignmentUpdateDTO,
} from '../models/assignmentDTOs';
import type { InitialOptions, SchoolOption, ExamOption } from '../models/filterDTOs';
import type { AssignClassOption } from '../models/classDTOs';

const LoadingOption = getZeroOption('Loading...');
const DefaultGradeLevelOption = getZeroOption('Select Grade...');
const DefaultExamTypeOption = getZeroOption('Select Assessment Type...');
const DefaultExamOption = getZeroOption('Select Assessment Name...');
const DefaultDistrictOption = getZeroOption('Select District...');
const DefaultSchoolOption = getZeroOption('Select School...');
const NoExamOption = getZeroOption('No Exams Available');
const DefaultClassOption = getZeroOption('Select Class...');
const NoClassOption = getZeroOption('No Classes Available');

const defaultFromDate = new Date();
const defaultToDate = new Date(defaultFromDate.getTime() + 14 * 24 * 60 * 60 * 1000); // +1 week

interface AllOptions {
    gradeLevelOptions: Option[];
    examTypeOptions: Option[];
    examOptions: Option[];
    districtOptions: Option[];
    schoolOptions: Option[];
    classOptions: AssignClassOption[];
}

interface CreateEditModalProps {
    open: boolean;
    modalMode: 'create' | 'edit' | 'view' | null;
    handleClose: (_shouldRefresh: boolean, _isInTimeout: boolean) => void;
    resourceId: number | undefined;
}

const CreateEditModal: React.FC<CreateEditModalProps> = ({
    open,
    modalMode,
    handleClose,
    resourceId,
}) => {
    const hasLoaded = useRef(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [isLoadingClasses, setIsLoadingClasses] = useState(false);
    const currentGradeLevelId = useRef<number | null>(null);
    const currentSchoolId = useRef<number | null>(null);
    const isViewOnly = modalMode === 'view';
    const [isReady, setIsReady] = useState(false);

    // Track last-fetched params to prevent duplicate API calls
    const lastExamFetchKey = useRef('');
    const lastClassFetchKey = useRef('');

    const [schoolData, setSchoolData] = useState<Array<SchoolOption>>([]);
    const [allOptions, setAllOptions] = useState<AllOptions>({
        gradeLevelOptions: [LoadingOption],
        examTypeOptions: [LoadingOption],
        examOptions: [LoadingOption],
        districtOptions: [LoadingOption],
        schoolOptions: [DefaultSchoolOption],
        classOptions: [],
    });

    // React Hook Form setup
    const {
        control,
        setValue,
        getValues,
        reset,
        watch,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(examAssignmentSchema),
        defaultValues: {
            gradeLevelMasterId: 0,
            examTypeMasterId: 0,
            examId: 0,
            districtId: 0,
            schoolId: 0,
            classes: [] as ClassAssignmentDTO[],
            numberOfAttemptsAllowed: 1,
            isSpanishAllowed: false,
            duration: 0,
            fromDTUTC: defaultFromDate,
            toDTUTC: defaultToDate,
        },
    });

    const gradeLevelId = watch('gradeLevelMasterId');
    const examTypeId = watch('examTypeMasterId');
    const examId = watch('examId');
    const districtId = watch('districtId');
    const schoolId = watch('schoolId');
    const classes = watch('classes');
    const [filteredStudentOptionsByClass, setFilteredStudentOptionsByClass] = useState<
        Record<number, any[]>
    >({});

    const sanitizeDate = (value: any) => {
        if (!value) return value;
        const date = value instanceof Date ? new Date(value) : new Date(value.toString());
        date.setSeconds(0, 0);
        return date;
    };

    const createEditUtcToLocal = (d: string | Date | undefined | null) => {
        if (!d) return null;
        const date = d instanceof Date ? d : new Date(d);
        if (isNaN(date.getTime())) return null;

        const local = new Date(date.getTime());
        local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
        return local;
    };

    const hasArchived = (classes || []).some((cls, _) => {
        const classOption = allOptions.classOptions.find((c) => c.classId === cls.classId);

        if (!classOption) return false; // if no class option, we can't determine archived status, so assume false to avoid blocking

        const studentOptions = [
            ...(classOption?.classStudents.map((s) => ({ id: s.studentId, archived: false })) ??
                []),
            ...(cls.students
                ?.filter((s) => !classOption?.classStudents.some((cs) => cs.studentId === s.id))
                .map((s) => ({ id: s.id, archived: true })) ?? []),
        ];

        const teacherOptions = [
            ...(classOption?.classTeachers.map((t) => ({ id: t.teacherId, archived: false })) ??
                []),
            ...(cls.teachers
                ?.filter((t) => !classOption?.classTeachers.some((ct) => ct.teacherId === t.id))
                .map((t) => ({ id: t.id, archived: true })) ?? []),
        ];

        return (
            cls.studentIds?.some((id) => studentOptions.find((s) => s.id === id)?.archived) ||
            cls.teacherIds?.some((id) => teacherOptions.find((t) => t.id === id)?.archived)
        );
    });

    const loadFilterOptions = async (): Promise<number> => {
        let firstDistrictId = 0;
        try {
            const res = await apiRequest<InitialOptions>(
                'GET',
                'utility/filterData?getGradeLevelMaster=true&getExamTypeMaster=true&getDistrict=true&getSchool=true'
            );
            const data = res.data;

            // Sort districts
            const sortedDistricts = data.districtOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
            );

            setAllOptions({
                gradeLevelOptions: [DefaultGradeLevelOption, ...data.gradeLevelMasterOptions],
                examTypeOptions: [DefaultExamTypeOption, ...data.examTypeMasterOptions],
                examOptions: [DefaultExamOption],
                districtOptions: [DefaultDistrictOption, ...sortedDistricts],
                schoolOptions: [DefaultSchoolOption],
                classOptions: [],
            });

            setSchoolData(data.schoolOptions || []);

            // Capture the first ID if available
            if (sortedDistricts.length > 0) {
                firstDistrictId = Number(sortedDistricts[0].id);
            }
        } catch {
            setError('Failed to fetch filter options. Please refresh the page or try again.');
        }
        hasLoaded.current = true;
        return firstDistrictId; // Return the ID
    };

    const loadAssignmentData = async (id: number) => {
        try {
            const res = await apiRequest<ExamAssignmentFullDTO>(
                'GET',
                `assessments/examAssignments/${id}`
            );
            const data = res.data;
            await fetchExamOptions(data.gradeLevelMasterId, data.examTypeMasterId);
            await fetchClassOptions(data.gradeLevelMasterId, data.schoolId);

            reset({
                examId: data.examId,
                districtId: data.districtId,
                schoolId: data.schoolId,
                numberOfAttemptsAllowed: data.numberOfAttemptsAllowed,
                fromDTUTC: createEditUtcToLocal(data.fromDate) || defaultFromDate,
                toDTUTC: createEditUtcToLocal(data.toDate) || defaultToDate,
                gradeLevelMasterId: data.gradeLevelMasterId,
                examTypeMasterId: data.examTypeMasterId,
                isSpanishAllowed: data.isSpanishAllowed,
                duration: data.duration,
                classes: data.classes.map((cls) => ({
                    classId: cls.classId,
                    studentIds: cls.students.map((s) => s.id),
                    teacherIds: cls.teachers.map((t) => t.id),
                    students: cls.students.map((s) => ({ id: s.id, name: s.name })),
                    teachers: cls.teachers.map((t) => ({ id: t.id, name: t.name })),
                })),
            });

            currentGradeLevelId.current = data.gradeLevelMasterId;
            currentSchoolId.current = data.schoolId;
        } catch {
            setError('Failed to load assessment data');
            hasLoaded.current = true;
        }
        hasLoaded.current = true;
    };

    const fetchExamOptions = async (gradeLevelMasterId: number, examTypeMasterId: number) => {
        const fetchKey = `${gradeLevelMasterId}-${examTypeMasterId}`;
        if (lastExamFetchKey.current === fetchKey) return;
        lastExamFetchKey.current = fetchKey;

        try {
            const res = await apiRequest<ExamOption[]>(
                'GET',
                `assessments/exams/forExamAssignment?gradeLevelMasterId=${gradeLevelMasterId}&examTypeMasterId=${examTypeMasterId}`
            );

            const examOptions: Option[] = (res.data || []).map((e) => ({
                id: e.examId,
                name: e.examName,
            }));
            if (examOptions.length > 0) {
                const sorted = examOptions.sort((a, b) =>
                    a.name.localeCompare(b.name, undefined, { numeric: true })
                );

                setAllOptions((prev) => ({ ...prev, examOptions: [DefaultExamOption, ...sorted] }));

                const currentExamId = getValues('examId');
                if (!sorted.some((e) => e.id === currentExamId)) {
                    setValue('examId', 0);
                }
            } else {
                setAllOptions((prev) => ({ ...prev, examOptions: [NoExamOption] }));
            }
        } catch {
            setError('Failed to load exams');
            setAllOptions((prev) => ({ ...prev, examOptions: [NoExamOption] }));
        }
    };

    const fetchSchoolOptions = (districtId: number, allSchools: any[]) => {
        if (!districtId) return [DefaultSchoolOption];

        const filteredSchools = allSchools.filter((s) => s.districtId === districtId);

        return [DefaultSchoolOption, ...filteredSchools];
    };

    const fetchClassOptions = async (gradeLevelId: number, schoolId: number) => {
        const fetchKey = `${gradeLevelId}-${schoolId}`;
        if (lastClassFetchKey.current === fetchKey) return;
        lastClassFetchKey.current = fetchKey;

        setIsLoadingClasses(true);
        try {
            const res = await apiRequest<AssignClassOption[]>(
                'GET',
                `/dbo/classes/classAssignment?gradeLevelMasterIds=${gradeLevelId}&schoolId=${schoolId}`
            );

            const data = res.data || [];
            if (data.length === 0) {
                setAllOptions((prev) => ({ ...prev, classOptions: [] }));
                return;
            }

            // Sort classes by name
            const sortedClasses = data
                .slice()
                .sort((a, b) => a.className.localeCompare(b.className));

            // Map into UI-friendly option objects
            const mappedClassOptions = sortedClasses.map((c) => ({
                classId: c.classId,
                className: c.className,
                classStudents: c.classStudents.map((s) => ({
                    studentId: s.studentId,
                    studentName: s.studentName,
                })),
                classTeachers: c.classTeachers.map((t) => ({
                    teacherId: t.teacherId,
                    teacherName: t.teacherName,
                })),
            }));
            setAllOptions((prev) => ({ ...prev, classOptions: mappedClassOptions }));
        } catch {
            setError('Failed to load classes');
            setAllOptions((prev) => ({ ...prev, classOptions: [] }));
        } finally {
            setIsLoadingClasses(false);
        }
    };

    useEffect(() => {
        if (!open) return;

        const init = async () => {
            setIsReady(false);
            setError('');

            // Reset dedup keys so fresh fetches occur on each modal open
            lastExamFetchKey.current = '';
            lastClassFetchKey.current = '';

            try {
                // 1. Wait for options and get the autofill ID
                const autoFillDistrictId = await loadFilterOptions();

                if ((modalMode === 'edit' || modalMode === 'view') && resourceId) {
                    await loadAssignmentData(resourceId);
                } else {
                    // 2. Pass the ID directly into reset
                    reset({
                        examId: 0,
                        // If creating, use the autofill ID. If not, use 0.
                        districtId: modalMode === 'create' ? autoFillDistrictId : 0,
                        schoolId: 0,
                        numberOfAttemptsAllowed: 1,
                        fromDTUTC: defaultFromDate,
                        toDTUTC: defaultToDate,
                        gradeLevelMasterId: 0,
                        examTypeMasterId: 0,
                        isSpanishAllowed: false,
                        duration: 0,
                        classes: [],
                    });
                }

                setIsReady(true);
            } catch (err) {
                console.error('Failed to load modal data', err);
                setError('Failed to load modal data.');
                setIsReady(true);
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, modalMode, resourceId, reset]);

    // Dynamically generate Exam Options
    useEffect(() => {
        if (!isReady) return; // Skip during initial load
        if (gradeLevelId > 0 && examTypeId > 0) {
            // Clear dedup key so the effect can fetch fresh options
            lastExamFetchKey.current = '';
            setAllOptions((prev) => ({ ...prev, examOptions: [LoadingOption] }));
            fetchExamOptions(gradeLevelId, examTypeId);
        } else {
            // Reset if not valid
            setAllOptions((prev) => ({ ...prev, examOptions: [DefaultExamOption] }));
            setValue('examId', 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gradeLevelId, examTypeId, isReady]);

    // Dynamically generate School Options
    useEffect(() => {
        if (!isReady) return; // Skip during initial load
        if (districtId > 0) {
            const newSchoolOptions = fetchSchoolOptions(districtId, schoolData);
            setAllOptions((prev) => ({ ...prev, schoolOptions: newSchoolOptions }));

            // Only auto-select if the current schoolId isn't valid in the new options
            const currentSchoolIdVal = getValues('schoolId');
            const isCurrentSchoolValid = newSchoolOptions.some(
                (s) => s.id === currentSchoolIdVal && currentSchoolIdVal !== 0
            );

            if (!isCurrentSchoolValid) {
                if (newSchoolOptions.length > 1) {
                    // Select the first real school option
                    setValue('schoolId', newSchoolOptions[1].id);
                } else {
                    setValue('schoolId', 0);
                }
            }
        } else {
            setAllOptions((prev) => ({ ...prev, schoolOptions: [DefaultSchoolOption] }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [districtId, schoolData, setValue, modalMode, isReady]);

    // Dynamically generate Classes/Students Options
    useEffect(() => {
        if (!isReady) return; // Skip during initial load
        if (gradeLevelId > 0 && schoolId > 0) {
            // Clear dedup key so the effect can fetch fresh options
            lastClassFetchKey.current = '';
            fetchClassOptions(gradeLevelId, schoolId);
        } else {
            setAllOptions((prev) => ({ ...prev, classOptions: [] }));
        }
    }, [gradeLevelId, schoolId, isReady]);

    useEffect(() => {
        if (modalMode !== 'edit') return;
        if (gradeLevelId !== currentGradeLevelId.current || schoolId !== currentSchoolId.current) {
            // Reset class options and students and teachers in all classes
            setValue('classes', []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gradeLevelId, schoolId]);

    // yup handles validation through handleSubmit in the form component
    const onSubmit = async () => {
        if (submitting || success) return;

        const values = getValues();
        const mapClasses = (clsArray: any[]): ClassAssignmentDTO[] => {
            return clsArray.map((cls) => {
                // get the class option to pull names
                const classOption = allOptions.classOptions.find((c) => c.classId === cls.classId);

                const students = (cls.studentIds || []).map((id: number) => {
                    // find student name from the classOption or fallback to empty string
                    const student = classOption?.classStudents.find((s) => s.studentId === id);
                    return { id, name: student?.studentName || '' };
                });

                const teachers = (cls.teacherIds || []).map((id: number) => {
                    const teacher = classOption?.classTeachers.find((t) => t.teacherId === id);
                    return { id, name: teacher?.teacherName || '' };
                });

                return {
                    classId: cls.classId,
                    studentIds: cls.studentIds || [],
                    teacherIds: cls.teacherIds || [],
                    students,
                    teachers,
                };
            });
        };

        if (modalMode === 'create') {
            const newExamAssignment: ExamAssignmentCreateDTO = {
                examId: values.examId,
                schoolId: values.schoolId,
                description: '', // Removed from regular EA
                numberOfAttemptsAllowed: values.numberOfAttemptsAllowed,
                fromDTUTC: sanitizeDate(values.fromDTUTC),
                toDTUTC: sanitizeDate(values.toDTUTC),
                examAssignmentTypeMasterId: Constants.EXAM_ASSIGNMENT_TYPE_MASTER_IDS.CLASS,
                gradeLevelMasterId: values.gradeLevelMasterId,
                examTypeMasterId: values.examTypeMasterId,
                isSpanishAllowed: values.isSpanishAllowed,
                duration: values.duration,
                classes: mapClasses(values.classes ?? []),
            };

            setSubmitting(true);
            setError('');
            try {
                await apiRequest<number>('POST', 'assessments/examAssignments', newExamAssignment);
                setSuccess(true);
                setTimeout(() => {
                    handleClose(true, false);
                    setSuccess(false);
                }, 750);
            } catch {
                setError('Failed to create assessment');
                return;
            } finally {
                setSubmitting(false);
            }
        }

        if (modalMode === 'edit') {
            if (!resourceId) {
                setError('Unable to load assessment Id');
                return;
            }

            const updateExamAssignment: ExamAssignmentUpdateDTO = {
                id: resourceId,
                examId: values.examId,
                schoolId: values.schoolId,
                description: '', // Removed from regular EA
                numberOfAttemptsAllowed: values.numberOfAttemptsAllowed,
                fromDTUTC: values.fromDTUTC,
                toDTUTC: values.toDTUTC,
                examAssignmentTypeMasterId: Constants.EXAM_ASSIGNMENT_TYPE_MASTER_IDS.CLASS,
                gradeLevelMasterId: values.gradeLevelMasterId,
                examTypeMasterId: values.examTypeMasterId,
                isSpanishAllowed: values.isSpanishAllowed,
                duration: values.duration,
                classes: mapClasses(values.classes ?? []),
            };

            setSubmitting(true);
            setError('');
            try {
                await apiRequest<number>(
                    'PUT',
                    `assessments/examAssignments/${resourceId}`,
                    updateExamAssignment
                );
                setSuccess(true);
                setTimeout(() => {
                    handleClose(true, false);
                    setSuccess(false);
                }, 750);
            } catch {
                setError('Failed to update assessment');
            } finally {
                setSubmitting(false);
            }
        }
    };

    return (
        <Modal open={open} onClose={() => handleClose(false, success)}>
            <Box
                sx={{
                    p: 4,
                    bgcolor: 'background.paper',
                    margin: 'auto',
                    mt: 8,
                    width: 800,
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    borderRadius: 2,
                }}
            >
                <h2>
                    {modalMode === 'create'
                        ? 'Add Assessment'
                        : modalMode === 'edit'
                          ? 'Edit Assessment'
                          : 'View Assessment'}
                </h2>
                {isViewOnly && (
                    <ErrorMessage error="This assessment has existing attempts and cannot be edited." />
                )}
                {hasArchived && !isViewOnly && (
                    <>
                        <br />
                        <ErrorMessage error="Please sync classes with archived students/teachers before submitting." />
                    </>
                )}
                <br />
                {!isReady ? (
                    <Loading message="Loading..." size="lg" />
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <ControlledSingleSelect
                                name="gradeLevelMasterId"
                                control={control}
                                options={allOptions.gradeLevelOptions}
                                error={errors.gradeLevelMasterId}
                                label="Grade Level"
                                onChange={(e, field) => {
                                    const value = Number(e.target.value);
                                    if (value !== 0) {
                                        setValue('gradeLevelMasterId', value);
                                        field.onChange(value);
                                    }
                                }}
                                disabled={isViewOnly}
                            />
                            <ControlledSingleSelect
                                name="examTypeMasterId"
                                control={control}
                                options={allOptions.examTypeOptions}
                                error={errors.examTypeMasterId}
                                label="Assessment Type"
                                onChange={(e, field) => {
                                    const value = Number(e.target.value);
                                    if (value !== 0) {
                                        setValue('examTypeMasterId', value);
                                        field.onChange(value);
                                    }
                                }}
                                disabled={isViewOnly}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 3 }}>
                                <ControlledSingleSelect
                                    name="examId"
                                    control={control}
                                    options={allOptions.examOptions}
                                    error={errors.examId}
                                    label="Assessment Name"
                                    onChange={(e, field) => {
                                        const value = Number(e.target.value);
                                        if (value !== 0) {
                                            setValue('examId', value);
                                            field.onChange(value);
                                        }
                                    }}
                                    disabled={isViewOnly}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                {examId !== 0 && (
                                    <Button
                                        onClick={() => null}
                                        sx={{
                                            textTransform: 'none',
                                        }}
                                        variant="outlined"
                                        href={`/reports/questionList/${examId}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View Assessment
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <ControlledSingleSelect
                                name="districtId"
                                control={control}
                                options={allOptions.districtOptions}
                                error={errors.districtId}
                                label="District"
                                onChange={(e, field) => {
                                    const value = Number(e.target.value);
                                    if (value !== 0) {
                                        setValue('districtId', value);
                                        field.onChange(value);
                                    }
                                }}
                                disabled={isViewOnly}
                            />
                            <ControlledSingleSelect
                                name="schoolId"
                                control={control}
                                options={allOptions.schoolOptions}
                                error={errors.schoolId}
                                label="School"
                                onChange={(e, field) => {
                                    const value = Number(e.target.value);
                                    if (value !== 0) {
                                        setValue('schoolId', value);
                                        field.onChange(value);
                                    }
                                }}
                                disabled={isViewOnly}
                            />
                        </Box>

                        {gradeLevelId > 0 && schoolId > 0 && (
                            <>
                                {isLoadingClasses && (
                                    <Loading message="Loading classes..." size="sm" />
                                )}
                                {!isLoadingClasses &&
                                    gradeLevelId > 0 &&
                                    schoolId > 0 &&
                                    allOptions.classOptions.length === 0 && (
                                        <ErrorMessage error="No classes available for the selected grade level and school." />
                                    )}

                                {!isLoadingClasses &&
                                    (classes || []).map((cls, index) => {
                                        const classOption = allOptions.classOptions.find(
                                            (c) => c.classId === cls.classId
                                        );
                                        const studentOptions = [
                                            // students from the class itself
                                            ...(classOption?.classStudents.map((s) => ({
                                                id: s.studentId,
                                                name: s.studentName,
                                                archived: false,
                                            })) || []),
                                            // students in the assessment but no longer in the class list
                                            ...(cls.students
                                                ?.filter(
                                                    (s) =>
                                                        !classOption?.classStudents.some(
                                                            (cs) => cs.studentId === s.id
                                                        )
                                                )
                                                .map((s) => ({
                                                    id: s.id,
                                                    name: s.name,
                                                    archived: true,
                                                })) || []),
                                        ];

                                        const teacherOptions = [
                                            // teachers from the class itself
                                            ...(classOption?.classTeachers.map((t) => ({
                                                id: t.teacherId,
                                                name: t.teacherName,
                                                archived: false,
                                            })) || []),
                                            // teachers in the assessment but no longer in the class list
                                            ...(cls.teachers
                                                ?.filter(
                                                    (t) =>
                                                        !classOption?.classTeachers.some(
                                                            (ct) => ct.teacherId === t.id
                                                        )
                                                )
                                                .map((t) => ({
                                                    id: t.id,
                                                    name: t.name,
                                                    archived: true,
                                                })) || []),
                                        ];

                                        const selectedClassIds = (classes ?? [])
                                            .map((c) => c.classId)
                                            .filter((id) => id !== cls.classId);
                                        const availableClassOptions = allOptions.classOptions
                                            .filter((c) => !selectedClassIds.includes(c.classId))
                                            .map((c) => ({ id: c.classId, name: c.className }));

                                        return (
                                            <Box
                                                key={cls.classId || index}
                                                sx={{
                                                    mb: 2,
                                                    border: '1px solid #ccc',
                                                    p: 2,
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <ControlledSingleSelect
                                                    name={`classes.${index}.classId`}
                                                    control={control}
                                                    label="Class"
                                                    options={[
                                                        allOptions.classOptions.length === 0
                                                            ? NoClassOption
                                                            : DefaultClassOption,
                                                        ...availableClassOptions,
                                                    ]}
                                                    error={errors.classes?.[index]?.classId}
                                                    onChange={(e, field) => {
                                                        const value = Number(e.target.value);
                                                        setValue(`classes.${index}.classId`, value);

                                                        // Reset students when class changes
                                                        setValue(`classes.${index}.students`, []);
                                                        setValue(`classes.${index}.studentIds`, []);

                                                        // Automatically assign ALL teachers from selected class
                                                        const selectedClass =
                                                            allOptions.classOptions.find(
                                                                (c) => c.classId === value
                                                            );
                                                        const allTeachers =
                                                            selectedClass?.classTeachers.map(
                                                                (t) => ({
                                                                    id: t.teacherId,
                                                                    name: t.teacherName,
                                                                })
                                                            ) || [];

                                                        setValue(
                                                            `classes.${index}.teachers`,
                                                            allTeachers
                                                        );
                                                        setValue(
                                                            `classes.${index}.teacherIds` as any,
                                                            allTeachers.map((t) => t.id)
                                                        );
                                                        field.onChange(value);
                                                    }}
                                                    disabled={
                                                        isViewOnly ||
                                                        allOptions.classOptions.length === 0
                                                    }
                                                />
                                                <ControlledChipMultiSelect
                                                    name={`classes.${index}.studentIds`}
                                                    control={control}
                                                    label={
                                                        !cls.classId || cls.classId === 0
                                                            ? 'Students (Select a class first)'
                                                            : studentOptions.length === 0
                                                              ? 'Students (No students available)'
                                                              : 'Students'
                                                    }
                                                    options={
                                                        filteredStudentOptionsByClass[index] ??
                                                        studentOptions
                                                    }
                                                    error={errors.classes?.[index]?.studentIds}
                                                    disabled={
                                                        isViewOnly ||
                                                        !cls.classId ||
                                                        cls.classId === 0
                                                    }
                                                    hideClear={isViewOnly}
                                                    renderExtras={() =>
                                                        !isViewOnly && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    gap: 1,
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                {modalMode === 'edit' && (
                                                                    <Tooltip
                                                                        title="Remove Archived Students & Teachers"
                                                                        arrow
                                                                    >
                                                                        <Box
                                                                            onClick={() => {
                                                                                const selectedStudentIds =
                                                                                    getValues(
                                                                                        `classes.${index}.studentIds`
                                                                                    ) || [];
                                                                                const selectedTeacherIds =
                                                                                    getValues(
                                                                                        `classes.${index}.teacherIds`
                                                                                    ) || [];

                                                                                // Filter active only
                                                                                const activeStudentIds =
                                                                                    selectedStudentIds.filter(
                                                                                        (id) => {
                                                                                            const opt =
                                                                                                studentOptions.find(
                                                                                                    (
                                                                                                        s
                                                                                                    ) =>
                                                                                                        s.id ===
                                                                                                        id
                                                                                                );
                                                                                            return (
                                                                                                opt &&
                                                                                                !opt.archived
                                                                                            );
                                                                                        }
                                                                                    );

                                                                                const activeTeacherIds =
                                                                                    selectedTeacherIds.filter(
                                                                                        (id) => {
                                                                                            const opt =
                                                                                                teacherOptions.find(
                                                                                                    (
                                                                                                        t
                                                                                                    ) =>
                                                                                                        t.id ===
                                                                                                        id
                                                                                                );
                                                                                            return (
                                                                                                opt &&
                                                                                                !opt.archived
                                                                                            );
                                                                                        }
                                                                                    );

                                                                                // Update form values
                                                                                setValue(
                                                                                    `classes.${index}.studentIds`,
                                                                                    activeStudentIds
                                                                                );
                                                                                setValue(
                                                                                    `classes.${index}.teacherIds`,
                                                                                    activeTeacherIds
                                                                                );

                                                                                // Update the filtered options to reflect only active
                                                                                setFilteredStudentOptionsByClass(
                                                                                    (prev) => ({
                                                                                        ...prev,
                                                                                        [index]:
                                                                                            studentOptions.filter(
                                                                                                (
                                                                                                    s
                                                                                                ) =>
                                                                                                    !s.archived
                                                                                            ),
                                                                                    })
                                                                                );
                                                                            }}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                cursor: hasArchived
                                                                                    ? 'pointer'
                                                                                    : 'not-allowed',
                                                                            }}
                                                                        >
                                                                            <SyncIcon
                                                                                style={{
                                                                                    width: 24,
                                                                                    height: 24,
                                                                                }}
                                                                                color={
                                                                                    hasArchived
                                                                                        ? 'red'
                                                                                        : 'gray'
                                                                                }
                                                                            />
                                                                        </Box>
                                                                    </Tooltip>
                                                                )}
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ minWidth: 100, px: 1 }}
                                                                    onClick={() => {
                                                                        // Only select active students
                                                                        const activeStudentIds =
                                                                            studentOptions
                                                                                .filter(
                                                                                    (s) =>
                                                                                        !s.archived
                                                                                ) // keep only ACTIVE
                                                                                .map((s) => s.id); // take their IDs

                                                                        setValue(
                                                                            `classes.${index}.studentIds`,
                                                                            activeStudentIds
                                                                        );
                                                                    }}
                                                                    disabled={
                                                                        studentOptions.length === 0
                                                                    }
                                                                >
                                                                    Select All
                                                                </Button>
                                                            </Box>
                                                        )
                                                    }
                                                />

                                                <ControlledChipDisplay
                                                    name={`classes.${index}.teacherIds`}
                                                    control={control}
                                                    label="Teachers"
                                                    options={teacherOptions}
                                                    error={errors.classes?.[index]?.teacherIds}
                                                    readOnly={true}
                                                />

                                                {!isViewOnly && (
                                                    <SimpleButton
                                                        type="button"
                                                        color="error"
                                                        onClick={() => {
                                                            const newClasses = [...(classes || [])];
                                                            newClasses.splice(index, 1);
                                                            setValue('classes', newClasses);
                                                        }}
                                                        disabled={isViewOnly}
                                                    >
                                                        Remove Class
                                                    </SimpleButton>
                                                )}
                                            </Box>
                                        );
                                    })}

                                {!isViewOnly && (
                                    <Box
                                        sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}
                                    >
                                        <SimpleButton
                                            type="button"
                                            variant="outlined"
                                            onClick={() => {
                                                const newClasses = [
                                                    ...(classes || []),
                                                    {
                                                        classId: 0,
                                                        students: [],
                                                        studentIds: [],
                                                        teachers: [],
                                                        teacherIds: [],
                                                    },
                                                ];
                                                setValue('classes', newClasses);
                                            }}
                                            disabled={isLoadingClasses}
                                        >
                                            Add Class
                                        </SimpleButton>
                                        {errors.classes && (
                                            <ErrorMessage error={errors.classes.message} />
                                        )}
                                    </Box>
                                )}
                            </>
                        )}

                        {/* Temporarily disable duration and number of attempts */}
                        {/* 
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <ControlledSingleSelect
                                    name="numberOfAttemptsAllowed"
                                    control={control}
                                    options={[1, 2, 3, 4, 5].map((n) => ({
                                        id: n,
                                        name: n.toString(),
                                    }))}
                                    error={errors.numberOfAttemptsAllowed}
                                    label="Number of Attempts"
                                    disabled={isViewOnly}
                                />
                            </Box>
                            <Box sx={{ flex: 1, marginTop: -2 }}>
                                <ControlledTextField
                                    name="duration"
                                    label="Duration (minutes)"
                                    control={control}
                                    error={errors.duration}
                                    type="number"
                                    variant="outlined"
                                    disabled={isViewOnly}
                                />
                                <FormHelperText>Enter 0 For No Time Limit</FormHelperText>
                            </Box>
                        </Box> */}
                        {/* Temporarily disable spanish exams */}
                        {/* <ControlledCheckbox
                            name="isSpanishAllowed"
                            control={control}
                            label="Spanish Allowed"
                            color="primary"
                            disabled={isViewOnly}
                        /> */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Controller
                                name="fromDTUTC"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <DateTimePicker
                                        {...field}
                                        label="Start Date & Time"
                                        onChange={(newValue) =>
                                            field.onChange(sanitizeDate(newValue))
                                        }
                                        slotProps={{
                                            textField: {
                                                error: !!fieldState.error,
                                                helperText: fieldState.error?.message,
                                                fullWidth: true,
                                                margin: 'normal',
                                            },
                                        }}
                                        disabled={isViewOnly}
                                    />
                                )}
                            />
                            <Controller
                                name="toDTUTC"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <DateTimePicker
                                        {...field}
                                        label="End Date & Time"
                                        onChange={(newValue) =>
                                            field.onChange(sanitizeDate(newValue))
                                        }
                                        slotProps={{
                                            textField: {
                                                error: !!fieldState.error,
                                                helperText: fieldState.error?.message,
                                                fullWidth: true,
                                                margin: 'normal',
                                            },
                                        }}
                                        disabled={isViewOnly}
                                    />
                                )}
                            />
                        </Box>

                        {!isViewOnly && (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: 2,
                                        mt: 2,
                                    }}
                                >
                                    <Box sx={{ mr: 'auto', alignSelf: 'flex-end' }}>
                                        {error ? (
                                            <ErrorMessage error={error} />
                                        ) : hasArchived ? (
                                            <ErrorMessage error="Please sync classes with archived students/teachers before submitting." />
                                        ) : null}
                                    </Box>

                                    <SimpleButton
                                        type="button"
                                        onClick={() => handleClose(false, false)}
                                        disabled={submitting || success}
                                        color="warning"
                                        variant="outlined"
                                    >
                                        Cancel
                                    </SimpleButton>

                                    <SimpleButton
                                        type="submit"
                                        disabled={submitting || success || hasArchived}
                                        variant="contained"
                                    >
                                        Submit
                                    </SimpleButton>
                                </Box>
                                <StatusMessage
                                    visible={success}
                                    status={`Assessment ${modalMode === 'create' ? 'created' : 'updated'} successfully!`}
                                    style={{ marginTop: 8 }}
                                />

                                <Loading visible={submitting} message="Processing..." size="sm" />
                            </>
                        )}
                    </form>
                )}
            </Box>
        </Modal>
    );
};

export default CreateEditModal;
